import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm } from 'redux-form';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import RenderTextField from '../../utils/renderFields/RenderTextField';
import RenderCheckboxField from '../../utils/renderFields/RenderCheckboxField';
import validate from './other/validate';
import countries from '../ImportCommands/utils/list/countryCode';
import RenderStockItem from '../stock/components/drawer/RenderStockItem';
import RenderSelectField from '../../utils/renderFields/RenderSelectField';
import { editProduct, closeReviewProductDialog } from './other/actions';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: 50px 0px;
`;

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3)
  }
}));

/**
 *
 * Passe les requiredAttributes pour pouvoir les afficher
 *
 * @param {Object} values            Objet contenant les requiredAttributes du serveur et de l'objet
 * @return {Object}                  Objet contenant les requiredAttributes du produit en fonction de ceux renvoyer par le serveur
 */
const objectToId = values =>
  values.requiredAttributes.filter(x => x.isPresent !== false).map(a => a.id);

/**
 *
 * Passe les requiredAttributes pour pouvoir les afficher
 *
 * @param  {Object} values                  Objet contenant les requiredAttributes de l'objet
 * @param  {Object} requiredAttributes      Objet contenant les requiredAttributes du serveur
 * @return {Object}                         Objet contenant les requiredAttributes du produit en fonction de ceux renvoyer par le serveur
 */
const isPresent = (values, requiredAttributes) => {
  if (!requiredAttributes) return values;
  values.forEach(element => {
    // eslint-disable-next-line no-param-reassign
    element.isPresent = !!requiredAttributes.find(x => x === element.id);
  });
  return values;
};

/**
 *
 * Componenent qui affiche le produit sous forme de form
 *
 * @param {Function} handleSubmit            Fonction qui permet de s'occuper de corriger et d'envoyer la form
 * @param {Object} requiredAttributes        Objet contenant les requiredAttributes du serveur
 * @param {Object} initialValues             Object contenant les photos d'origines
 */
let ProductForm = ({ handleSubmit, requiredAttributes, initialValues }) => {
  const classes = useStyles();
  return (
    <form className={classes.form} onSubmit={handleSubmit} noValidate>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="name"
            id="name"
            label="Nom"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="barcodeValue"
            id="barcodeValue"
            label="Code barre"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="forecastQuantity"
            id="forecastQuantity"
            label="Quantité Prévisionnelle"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            disabled
            component={RenderTextField}
          />
        </Grid>

        <Grid item md={6}>
          <Field
            name="quantity"
            id="quantity"
            label="Quantité"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="size"
            id="size"
            label="Taille"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="color"
            id="color"
            label="Couleur"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="value"
            id="value"
            label="Valeur"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="material"
            id="material"
            label="Matériel"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="reference"
            id="reference"
            label="Référence"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="countryOfOrigin"
            id="countryOfOrigin"
            label="Frabriqué en"
            datas={countries}
            fullWidth
            displayErrorBeforeSubmit
            component={RenderSelectField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={12}>
          <Field
            name="insuranceRequired"
            id="insuranceRequired"
            label="Assurer les produits à la valeur indiquée"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderCheckboxField}
          />
        </Grid>
      </Grid>
      <h2>Les attributs requis</h2>
      <FieldArray
        name="requiredAttributes"
        component={RenderStockItem}
        requiredAttributes={initialValues.requiredAttributes}
        currentStockItem={requiredAttributes}
      />
      <ButtonContainer>
        <Button variant="contained" color="secondary" type="submit">
          Modifier ce produit
        </Button>
      </ButtonContainer>
    </form>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchEditProduct: item => dispatch(editProduct(item))
});

const mapStateToProps = state => {
  const {
    id,
    name,
    color,
    size,
    material,
    value,
    barcodeValue,
    reference,
    forecastQuantity,
    quantity,
    countryOfOrigin,
    insuranceRequired
  } = state.importProducts.currentProduct;
  return {
    id,
    requiredAttributes: state.importProducts.currentProduct.requiredAttributes,
    products: state.importProducts.products,
    eanInFile: state.importProducts.eanInFile,
    barcodeValues: state.importProducts.barcodeValues,
    initialValues: {
      name,
      color,
      size,
      material,
      value,
      barcodeValue,
      reference,
      forecastQuantity,
      quantity,
      countryOfOrigin,
      insuranceRequired,
      isInsuranceRequired: insuranceRequired,
      requiredAttributes: isPresent(
        state.importProducts.requiredAttributes,
        state.importProducts.currentProduct.requiredAttributes
      )
    }
  };
};

ProductForm = reduxForm({
  form: 'updateProductForm',
  validate,
  onSubmit: (values, dispatch) => {
    const product = {
      ...values,
      requiredAttributes: objectToId(values),
      quantity: +values.quantity
    };
    dispatch(closeReviewProductDialog(product));
  }
})(ProductForm);

ProductForm.propTypes = {
  handleSubmit: PropTypes.func,
  requiredAttributes: PropTypes.array,
  initialValues: PropTypes.object
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProductForm)
);
